import { useEffect, useState } from 'react';
import { Input, InputNumber, Modal as AntModal, notification } from 'antd';

import { Button, Icon } from 'components/common';
import { colours } from 'utils/theme';
import { usePost, usePut } from 'hooks';
import { createAccountProduct, editAccountProduct } from 'services/api';
import { errors } from '../constants';
import { FieldLabel, FieldList, FieldRow, ErrorText } from '../styled';

const emptyEntries = () => [''];

const StringListEditor = ({ label, entries, onChange, testIdPrefix }) => (
  <>
    <FieldLabel>{label}</FieldLabel>
    <FieldList>
      {entries.map((entry, index) => (
        <FieldRow key={`${testIdPrefix}-${index}`}>
          <Input
            value={entry}
            onChange={e => {
              const next = [...entries];
              next[index] = e.target.value;
              onChange(next);
            }}
            placeholder={`Add ${label.toLowerCase()}`}
            data-testid={`${testIdPrefix}-${index}`}
          />
          <Button
            size='small'
            onClick={() => {
              if (entries.length === 1) {
                onChange(['']);
                return;
              }
              onChange(entries.filter((_, i) => i !== index));
            }}
            data-testid={`${testIdPrefix}-${index}-remove`}
          >
            <Icon icon='cancel' size='sm' color={colours.darkGrey} />
          </Button>
        </FieldRow>
      ))}
      <Button
        size='small'
        onClick={() => onChange([...entries, ''])}
        data-testid={`${testIdPrefix}-add`}
      >
        + Add
      </Button>
    </FieldList>
  </>
);

const sanitize = list => list.map(s => s.trim()).filter(s => s.length > 0);

const AddEditProduct = ({ product, setModal, refresh }) => {
  const isEdit = Boolean(product);
  const [name, setName] = useState(product?.name ?? '');
  const [description, setDescription] = useState(product?.description ?? '');
  const [valueProps, setValueProps] = useState(
    product?.valueProps?.length ? product.valueProps : emptyEntries()
  );
  const [differentiators, setDifferentiators] = useState(
    product?.differentiators?.length ? product.differentiators : emptyEntries()
  );
  const [competingProducts, setCompetingProducts] = useState(
    product?.competingProducts?.length
      ? product.competingProducts
      : emptyEntries()
  );
  const [sortOrder, setSortOrder] = useState(product?.sortOrder ?? 0);
  const [validationErrors, setValidationErrors] = useState({});

  const [
    { res: createRes, err: createErr, loading: creating },
    postCreate,
  ] = usePost({ url: createAccountProduct });
  const [{ res: editRes, err: editErr, loading: editing }, putEdit] = usePut(
    isEdit ? { url: editAccountProduct(product.id) } : {}
  );

  const inFlight = creating || editing;
  const successRes = createRes || editRes;
  const errRes = createErr || editErr;

  useEffect(() => {
    if (successRes) {
      notification.success({
        message: isEdit ? 'Product updated' : 'Product created',
      });
      setModal('');
      refresh();
    }
  }, [successRes, isEdit, setModal, refresh]);

  useEffect(() => {
    if (errRes) {
      notification.error({
        message: 'Could not save product',
        description: errRes.message ?? 'Unexpected error',
      });
    }
  }, [errRes]);

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = errors.nameRequired;
    if (!description.trim()) e.description = errors.descriptionRequired;
    if (!sanitize(valueProps).length) e.valueProps = errors.valuePropsRequired;
    if (!sanitize(differentiators).length)
      e.differentiators = errors.differentiatorsRequired;
    if (!sanitize(competingProducts).length)
      e.competingProducts = errors.competingProductsRequired;
    setValidationErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleOk = () => {
    if (!validate()) return;

    const body = {
      name: name.trim(),
      description: description.trim(),
      valueProps: sanitize(valueProps),
      differentiators: sanitize(differentiators),
      competingProducts: sanitize(competingProducts),
      sortOrder: isEdit ? sortOrder : undefined,
    };

    if (isEdit) {
      putEdit({ body });
    } else {
      postCreate({ body });
    }
  };

  return (
    <AntModal
      open
      width={720}
      title={isEdit ? 'Edit Product' : 'Add Product'}
      onCancel={() => setModal('')}
      onOk={handleOk}
      okButtonProps={{ loading: inFlight }}
      okText={isEdit ? 'Save' : 'Create'}
    >
      <FieldLabel>Name</FieldLabel>
      <Input
        value={name}
        onChange={e => setName(e.target.value)}
        maxLength={200}
        data-testid='product-name'
      />
      {validationErrors.name && <ErrorText>{validationErrors.name}</ErrorText>}

      <FieldLabel>Description</FieldLabel>
      <Input.TextArea
        value={description}
        onChange={e => setDescription(e.target.value)}
        rows={3}
        data-testid='product-description'
      />
      {validationErrors.description && (
        <ErrorText>{validationErrors.description}</ErrorText>
      )}

      <StringListEditor
        label='Value props'
        entries={valueProps}
        onChange={setValueProps}
        testIdPrefix='product-valueprop'
      />
      {validationErrors.valueProps && (
        <ErrorText>{validationErrors.valueProps}</ErrorText>
      )}

      <StringListEditor
        label='Differentiators'
        entries={differentiators}
        onChange={setDifferentiators}
        testIdPrefix='product-differentiator'
      />
      {validationErrors.differentiators && (
        <ErrorText>{validationErrors.differentiators}</ErrorText>
      )}

      <StringListEditor
        label='Competing products'
        entries={competingProducts}
        onChange={setCompetingProducts}
        testIdPrefix='product-competing'
      />
      {validationErrors.competingProducts && (
        <ErrorText>{validationErrors.competingProducts}</ErrorText>
      )}

      {isEdit && (
        <>
          <FieldLabel>Sort order</FieldLabel>
          <InputNumber
            min={0}
            value={sortOrder}
            onChange={value => setSortOrder(value ?? 0)}
            data-testid='product-sort-order'
          />
        </>
      )}
    </AntModal>
  );
};

export default AddEditProduct;
